import type { ISegmentExport } from '@feathr/blackbox';

/**
 * The visual mode for the Progress column cell.
 *
 * - `notApplicable`: render plain "N/A" text — used for stopped/failed rows.
 * - `calculating`: render "Calculating…" text only — Phase 1 (`phase=enumerating`,
 *   `total_chunks=null`) when the BE doesn't yet know the denominator.
 * - `complete`: render a full green bar at 100% — `state=ready` or the
 *   `total_chunks=0` empty-segment edge case.
 * - `inProgress`: render a determinate bar at `percent`% — Phase 2
 *   (`phase=exporting`) with a known denominator.
 */
export type TProgressMode =
  | { kind: 'notApplicable' }
  | { kind: 'calculating' }
  | { kind: 'complete' }
  | { kind: 'inProgress'; percent: number };

interface IProgressInputs {
  state: ISegmentExport['state'];
  phase: ISegmentExport['phase'];
  totalChunks: ISegmentExport['total_chunks'];
  currentChunkIndex: ISegmentExport['current_chunk_index'];
}

/**
 * Derives the visual mode for a row's Progress column from BE phase + chunk fields.
 *
 * Ordering matters: terminal-but-not-ready (`stopped`/`failed`) wins over phase data,
 * `ready` always shows 100%, the `total_chunks=0` empty-segment case is treated as
 * complete to avoid divide-by-zero, and only then do we branch on `phase`.
 *
 * @param inputs - The relevant subset of `ISegmentExport` fields.
 * @returns The progress display mode.
 *
 * @example
 *   getProgressMode({ state: 'running', phase: 'exporting', totalChunks: 4, currentChunkIndex: 1 })
 *   // → { kind: 'inProgress', percent: 25 }
 */
export function getProgressMode({
  state,
  phase,
  totalChunks,
  currentChunkIndex,
}: IProgressInputs): TProgressMode {
  if (state === 'stopped' || state === 'failed') {
    return { kind: 'notApplicable' };
  }

  if (state === 'ready' || totalChunks === 0) {
    return { kind: 'complete' };
  }

  if (phase === 'enumerating' || totalChunks === null || totalChunks === undefined) {
    return { kind: 'calculating' };
  }

  const percent = Math.min(100, Math.round((currentChunkIndex / totalChunks) * 100));
  return { kind: 'inProgress', percent };
}
